import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Table, Button, Modal, Form, Input, InputNumber, Select, DatePicker, Checkbox, Tag, Space, Typography, Popconfirm, Drawer, Descriptions, notification, Alert } from 'antd';
import { PlusOutlined, MinusCircleOutlined, CheckOutlined, UndoOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useBooks, BooksSelector, BooksCaption } from '../../lib/books';

import { date as fmtDate, label } from '../../lib/format';
import { CurrencySelect } from '../../components/CountrySelect';
const money = (v) => Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const STATUS_COLORS = { DRAFT: 'default', POSTED: 'green', REVERSED: 'orange' };
const TREATMENTS = [
  { value: 'R', label: 'R - Recoverable' },
  { value: 'NR', label: 'NR - Non recoverable' },
  { value: 'A', label: 'A - Apportionment' },
];
const OSS = [
  { value: 'U', label: 'U - Union scheme' },
  { value: 'NU', label: 'NU - Non-Union scheme' },
  { value: 'I', label: 'I - Import scheme' },
];

/**
 * Transaction Posting (xlsx "Tran. posting"): the journal register and the
 * posting form. Each row is entered the way the sheet reads - Dr account,
 * Cr account, amount, VAT code - and the server expands it into balanced
 * ledger lines (shown in the preview before posting).
 */
export default function JournalPage() {
  const auth = useAuth();
  const isManager = auth.user.role === 'SYSTEM_ADMIN' || auth.user.role === 'COMPANY_MANAGER';
  const { ledgerId } = useBooks();
  const [rows, setRows] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState(null);
  const [refs, setRefs] = React.useState({ accounts: [], codes: [], vatCodes: [], departments: [], branches: [], projects: [], counterparties: [] });
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [detail, setDetail] = React.useState(null);
  const [form] = Form.useForm();
  const [params] = useSearchParams();

  // Deep link (e.g. from the year-end close list): /journal?entry=<id> opens that entry.
  React.useEffect(() => {
    const id = params.get('entry');
    if (id) request.get(`/journal/${id}`).then((r) => r.json()).then((r) => r.status && setDetail(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!ledgerId) return;
    const q = { ledger_id: ledgerId };
    Promise.all([
      request.get('/accounts', q).then((r) => r.json()),
      request.get('/vat-setup/transaction-codes', q).then((r) => r.json()),
      request.get('/vat-setup/vat-codes', q).then((r) => r.json()),
      request.get('/departments', q).then((r) => r.json()),
      request.get('/branches', q).then((r) => r.json()),
      request.get('/projects', q).then((r) => r.json()),
      request.get('/counterparties', { ...q, status: 'ACTIVE' }).then((r) => r.json()),
    ]).then(([a, c, v, d, b, p, cp]) =>
      setRefs({
        accounts: a.status ? a.data.filter((x) => !x.is_heading) : [],
        codes: c.status ? c.data : [],
        vatCodes: v.status ? v.data : [],
        departments: d.status ? d.data : [],
        branches: b.status ? b.data : [],
        projects: p.status ? p.data : [],
        counterparties: cp.status ? cp.data : [],
      })
    );
  }, [ledgerId]);

  const fetchRows = () => {
    if (!ledgerId) return setRows([]);
    setLoading(true);
    request
      .get('/journal', { ledger_id: ledgerId, ...(statusFilter ? { status: statusFilter } : {}) })
      .then((r) => r.json())
      .then((r) => r.status && setRows(r.data))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchRows, [ledgerId, statusFilter]);

  const accountOptions = refs.accounts.map((a) => ({ value: a.id, label: `${a.code} ${a.name}` }));
  const codeOptions = refs.codes.map((c) => ({ value: c.id, label: `${c.code} - ${c.name}` }));
  const codePolarity = (id) => refs.codes.find((c) => c.id === id)?.polarity;

  const payload = (values) => ({
    ledger_id: ledgerId,
    entry_date: values.entry_date.format('YYYY-MM-DD'),
    transaction_code_id: values.transaction_code_id || null,
    reference: values.reference,
    currency: (values.currency || 'EUR').toUpperCase(),
    exchange_rate: values.exchange_rate || 1,
    details: values.details,
    rows: (values.rows || []).map((r) => ({ ...r, counterparty_name: r.counterparty_name || null, details: r.details || null })),
  });

  const openNew = () => {
    setEditing(null);
    setPreview(null);
    form.resetFields();
    form.setFieldsValue({ entry_date: moment(), currency: 'EUR', exchange_rate: 1, rows: [{ amount: null }] });
    setModalOpen(true);
  };

  const doPreview = () => {
    form.validateFields().then((values) => {
      request
        .post('/journal/preview', payload(values))
        .then((r) => r.json())
        .then((r) => (r.status ? setPreview(r.data) : notification.error({ message: r.message })));
    });
  };

  const save = (post) => {
    form.validateFields().then((values) => {
      setBusy(true);
      const body = { ...payload(values), post };
      (editing ? request.put(`/journal/${editing.id}`, { ...body, ledger_id: undefined, post: undefined }) : request.post('/journal', body))
        .then((r) => r.json())
        .then(async (r) => {
          if (!r.status) return notification.error({ message: r.message });
          if (editing && post) {
            const p = await request.post(`/journal/${editing.id}/post`, {}).then((x) => x.json());
            if (!p.status) return notification.error({ message: p.message });
          }
          notification.success({ message: `${r.data.entry_number} ${post ? 'posted' : 'saved as draft'}` });
          setModalOpen(false);
          fetchRows();
        })
        .finally(() => setBusy(false));
    });
  };

  const act = (entry, action, body) =>
    request
      .post(`/journal/${entry.id}/${action}`, body || {})
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: `${r.data.entry_number} ${r.data.status.toLowerCase()}` });
          fetchRows();
          if (detail) setDetail(null);
        } else notification.error({ message: r.message });
      });

  const remove = (entry) =>
    request
      .delete(`/journal/${entry.id}`)
      .then((r) => r.json())
      .then((r) => (r.status ? fetchRows() : notification.error({ message: r.message })));

  const openDetail = (entry) => request.get(`/journal/${entry.id}`).then((r) => r.json()).then((r) => r.status && setDetail(r.data));

  const columns = [
    { title: 'Number', dataIndex: 'entry_number', width: 130, render: (v, e) => <Typography.Link onClick={() => openDetail(e)}>{v}</Typography.Link> },
    { title: 'Date', dataIndex: 'entry_date', width: 110, sorter: (a, b) => (a.entry_date < b.entry_date ? -1 : 1), render: (v) => fmtDate(v) },
    { title: 'Code', key: 'code', width: 80, render: (_, e) => (e.TransactionCode ? <Tag>{e.TransactionCode.code}</Tag> : '-') },
    { title: 'Reference', dataIndex: 'reference', render: (v) => v || '-' },
    { title: 'Details', dataIndex: 'details', render: (v) => v || '-' },
    { title: 'Currency', dataIndex: 'currency', width: 80, render: (v, e) => (v === 'EUR' ? v : `${v} @ ${Number(e.exchange_rate)}`) },
    { title: 'Debit', dataIndex: 'total_debit', align: 'right', width: 110, render: money },
    { title: 'Credit', dataIndex: 'total_credit', align: 'right', width: 110, render: money },
    { title: 'Status', dataIndex: 'status', width: 100, render: (v) => <Tag color={STATUS_COLORS[v]}>{label(v)}</Tag> },
    {
      title: '',
      key: 'actions',
      width: 130,
      render: (_, e) => (
        <Space size={4}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(e)} title="Lines" />
          {e.status === 'DRAFT' && <Button size="small" icon={<CheckOutlined />} onClick={() => act(e, 'post')} title="Post" />}
          {e.status === 'DRAFT' && (
            <Popconfirm title="Delete this draft?" onConfirm={() => remove(e)}>
              <Button size="small" danger icon={<DeleteOutlined />} title="Delete draft" />
            </Popconfirm>
          )}
          {e.status === 'POSTED' && isManager && (
            <Popconfirm title="Reverse this entry with an equal and opposite posting dated today?" onConfirm={() => act(e, 'reverse', { date: moment().format('YYYY-MM-DD') })}>
              <Button size="small" icon={<UndoOutlined />} title="Reverse" />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 16 }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Transaction Posting
          </Typography.Title>
          <div><BooksCaption /></div>
          <Typography.Text type="secondary">Journal entries: enter each transaction as Dr account / Cr account / amount / VAT code; the ledger lines are generated and balanced.</Typography.Text>
        </div>
        <Space wrap>
          <BooksSelector />
          <Input.Search placeholder="Search number, reference, details" allowClear style={{ width: 240 }} onSearch={setSearch} onChange={(e) => !e.target.value && setSearch('')} />
          <Select allowClear style={{ width: 140 }} placeholder="All statuses" value={statusFilter} onChange={setStatusFilter} options={Object.keys(STATUS_COLORS).map((s) => ({ value: s, label: s }))} />
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew} disabled={!ledgerId}>
            New Transaction
          </Button>
        </Space>
      </div>

      <Table
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={search ? rows.filter((e) => [e.entry_number, e.reference, e.details, e.TransactionCode?.code].some((x) => x && String(x).toLowerCase().includes(search.toLowerCase()))) : rows}
        loading={loading}
        pagination={{ pageSize: 25, showTotal: (total) => String(total) + ' in total' }}
        locale={{ emptyText: search ? 'No entries match your search' : 'No entries yet' }}
      />

      <Modal
        title={editing ? `Edit ${editing.entry_number}` : 'New Transaction'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        width={1180}
        footer={[
          <Button key="preview" onClick={doPreview}>
            Preview ledger lines
          </Button>,
          <Button key="draft" onClick={() => save(false)} loading={busy}>
            Save draft
          </Button>,
          <Button key="post" type="primary" icon={<CheckOutlined />} onClick={() => save(true)} loading={busy}>
            Post
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical">
          <Space style={{ display: 'flex' }} align="start" wrap>
            <Form.Item name="entry_date" label="Tran. date" rules={[{ required: true }]} style={{ width: 150 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="transaction_code_id" label="Tran. code" rules={[{ required: true }]} style={{ width: 260 }}>
              <Select options={codeOptions} showSearch optionFilterProp="label" />
            </Form.Item>
            <Form.Item name="reference" label="Tran. reference" style={{ width: 200 }}>
              <Input maxLength={120} placeholder="Invoice / document no." />
            </Form.Item>
            <Form.Item name="currency" label="Currency" style={{ width: 170 }}>
              <CurrencySelect />
            </Form.Item>
            <Form.Item name="exchange_rate" label="Exch. rate to EUR" style={{ width: 130 }}>
              <InputNumber min={0.000001} step={0.0001} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="details" label="Details" style={{ width: 300 }}>
              <Input maxLength={255} />
            </Form.Item>
          </Space>

          <Form.Item noStyle shouldUpdate={(a, b) => a.transaction_code_id !== b.transaction_code_id}>
            {({ getFieldValue }) => {
              const polarity = codePolarity(getFieldValue('transaction_code_id'));
              return (
                <Form.List name="rows">
                  {(fields, { add, remove }) => (
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 110px 150px 70px 130px 120px 1fr 32px', gap: 8, fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                        <span>Dr account</span><span>Cr account</span><span>Amount</span><span>VAT code</span><span>VAT %</span><span>{polarity === 'INPUT' ? 'Input treatment' : 'OSS'}</span><span>Analysis</span><span>Counterparty / details</span><span />
                      </div>
                      {fields.map((field) => (
                        <div key={field.key} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 110px 150px 70px 130px 120px 1fr 32px', gap: 8, alignItems: 'start' }}>
                          <Form.Item {...field} name={[field.name, 'dr_account_id']} rules={[{ required: true, message: 'Dr' }]}>
                            <Select showSearch optionFilterProp="label" options={accountOptions} placeholder="Debit" />
                          </Form.Item>
                          <Form.Item {...field} name={[field.name, 'cr_account_id']} rules={[{ required: true, message: 'Cr' }]}>
                            <Select showSearch optionFilterProp="label" options={accountOptions} placeholder="Credit" />
                          </Form.Item>
                          <Form.Item {...field} name={[field.name, 'amount']} rules={[{ required: true, message: 'Amount' }]}>
                            <InputNumber min={0.01} precision={2} style={{ width: '100%' }} placeholder="Net" />
                          </Form.Item>
                          <Form.Item {...field} name={[field.name, 'vat_code_id']}>
                            <Select
                              allowClear
                              showSearch
                              optionFilterProp="label"
                              placeholder="None"
                              options={refs.vatCodes.map((v) => ({ value: v.id, label: `${v.code} - ${v.rate}%` }))}
                              onChange={(id) => {
                                const list = form.getFieldValue('rows');
                                const vc = refs.vatCodes.find((v) => v.id === id);
                                list[field.name].vat_rate = vc ? Number(vc.rate) : null;
                                form.setFieldsValue({ rows: list });
                              }}
                            />
                          </Form.Item>
                          <Form.Item {...field} name={[field.name, 'vat_rate']}>
                            <InputNumber min={0} max={100} style={{ width: '100%' }} />
                          </Form.Item>
                          {polarity === 'INPUT' ? (
                            <Form.Item {...field} name={[field.name, 'vat_treatment']}>
                              <Select allowClear placeholder="R" options={TREATMENTS} />
                            </Form.Item>
                          ) : (
                            <Form.Item {...field} name={[field.name, 'oss_scheme']}>
                              <Select allowClear placeholder="-" options={OSS} />
                            </Form.Item>
                          )}
                          <Space direction="vertical" size={2}>
                            <Form.Item {...field} name={[field.name, 'department_id']} noStyle>
                              <Select allowClear size="small" placeholder="Dept." style={{ width: 120 }} options={refs.departments.map((d) => ({ value: d.id, label: d.name }))} />
                            </Form.Item>
                            <Form.Item {...field} name={[field.name, 'branch_id']} noStyle>
                              <Select allowClear size="small" placeholder="Branch" style={{ width: 120 }} options={refs.branches.map((b) => ({ value: b.id, label: `${b.code} ${b.name}` }))} />
                            </Form.Item>
                            <Form.Item {...field} name={[field.name, 'project_id']} noStyle>
                              <Select allowClear size="small" placeholder="Project" style={{ width: 120 }} options={refs.projects.map((p) => ({ value: p.id, label: p.name }))} />
                            </Form.Item>
                          </Space>
                          <Space direction="vertical" size={2}>
                            <Form.Item {...field} name={[field.name, 'counterparty_id']} noStyle>
                              <Select allowClear showSearch size="small" optionFilterProp="label" placeholder="Customer / supplier" style={{ width: '100%' }} options={refs.counterparties.map((c) => ({ value: c.id, label: `${c.code} ${c.name}` }))} />
                            </Form.Item>
                            <Form.Item {...field} name={[field.name, 'counterparty_name']} noStyle>
                              <Input size="small" placeholder="or a one-off name" maxLength={150} />
                            </Form.Item>
                            <Form.Item {...field} name={[field.name, 'details']} noStyle>
                              <Input size="small" placeholder="Line details" maxLength={255} />
                            </Form.Item>
                            <Form.Item {...field} name={[field.name, 'related_party']} valuePropName="checked" noStyle>
                              <Checkbox style={{ fontSize: 12 }}>Related party</Checkbox>
                            </Form.Item>
                          </Space>
                          <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(field.name)} />
                        </div>
                      ))}
                      <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ amount: null })}>
                        Add row
                      </Button>
                    </div>
                  )}
                </Form.List>
              );
            }}
          </Form.Item>
        </Form>

        {preview && (
          <div style={{ marginTop: 16 }}>
            <Typography.Text strong>Ledger lines that will be posted</Typography.Text>
            <Table
              size="small"
              rowKey="line_no"
              pagination={false}
              dataSource={preview}
              style={{ marginTop: 6 }}
              columns={[
                { title: '#', dataIndex: 'line_no', width: 40 },
                { title: 'Row', dataIndex: 'row_no', width: 50 },
                { title: 'Account', dataIndex: 'account' },
                { title: 'Debit', dataIndex: 'debit', align: 'right', render: money },
                { title: 'Credit', dataIndex: 'credit', align: 'right', render: money },
                { title: 'VAT', key: 'vat', render: (_, l) => (l.is_vat_line ? <Tag color="purple">VAT control</Tag> : l.vat_amount ? `${money(l.vat_amount)} @ ${l.vat_rate}%` : '') },
              ]}
              summary={(d) => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <strong>Totals</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <strong>{money(d.reduce((s, l) => s + Number(l.debit), 0))}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <strong>{money(d.reduce((s, l) => s + Number(l.credit), 0))}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} />
                </Table.Summary.Row>
              )}
            />
          </div>
        )}
      </Modal>

      <Drawer title={detail ? `${detail.entry_number} - ${detail.status}` : ''} open={!!detail} onClose={() => setDetail(null)} width={900}>
        {detail && (
          <div>
            <Descriptions size="small" column={3} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Date">{detail.entry_date}</Descriptions.Item>
              <Descriptions.Item label="Code">{detail.TransactionCode ? `${detail.TransactionCode.code} - ${detail.TransactionCode.name}` : '-'}</Descriptions.Item>
              <Descriptions.Item label="Reference">{detail.reference || '-'}</Descriptions.Item>
              <Descriptions.Item label="Currency">{detail.currency} @ {Number(detail.exchange_rate)}</Descriptions.Item>
              <Descriptions.Item label="Posted">{detail.posted_at ? `${moment(detail.posted_at).format('YYYY-MM-DD HH:mm')} by ${detail.PostedBy?.name || '-'}` : '-'}</Descriptions.Item>
              <Descriptions.Item label="Source">{detail.source}</Descriptions.Item>
              <Descriptions.Item label="Details" span={3}>{detail.details || '-'}</Descriptions.Item>
            </Descriptions>
            {detail.ReversalOf && <Alert type="info" showIcon style={{ marginBottom: 12 }} message={`Reversal of ${detail.ReversalOf.entry_number}`} />}
            {detail.ReversedBy && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={`Reversed by ${detail.ReversedBy.entry_number}`} />}
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={detail.JournalLines}
              columns={[
                { title: '#', dataIndex: 'line_no', width: 40 },
                { title: 'Account', key: 'acc', render: (_, l) => `${l.Account?.code} ${l.Account?.name}` },
                { title: 'Debit', dataIndex: 'debit', align: 'right', render: money },
                { title: 'Credit', dataIndex: 'credit', align: 'right', render: money },
                { title: 'Contra', key: 'contra', render: (_, l) => (l.ContraAccount ? `${l.ContraAccount.code} ${l.ContraAccount.name}` : '-') },
                { title: 'VAT', key: 'vat', render: (_, l) => (l.is_vat_line ? <Tag color="purple">VAT control</Tag> : l.VatCode ? `${l.VatCode.code} @ ${Number(l.vat_rate)}%${l.vat_amount ? ` = ${money(l.vat_amount)}` : ''}${l.vat_treatment ? ` (${l.vat_treatment})` : ''}` : '-') },
                { title: 'Analysis', key: 'an', render: (_, l) => [l.Department?.name, l.Branch?.name, l.Project?.name].filter(Boolean).join(' / ') || '-' },
                { title: 'Details', key: 'd', render: (_, l) => [l.Counterparty ? `${l.Counterparty.code} ${l.Counterparty.name}` : l.counterparty_name, l.details].filter(Boolean).join(' - ') || '-' },
              ]}
            />
            <Space style={{ marginTop: 12 }}>
              {detail.status === 'DRAFT' && (
                <Button type="primary" icon={<CheckOutlined />} onClick={() => act(detail, 'post')}>
                  Post
                </Button>
              )}
              {detail.status === 'POSTED' && isManager && (
                <Popconfirm title="Reverse this entry with an equal and opposite posting dated today?" onConfirm={() => act(detail, 'reverse', { date: moment().format('YYYY-MM-DD') })}>
                  <Button icon={<UndoOutlined />}>Reverse</Button>
                </Popconfirm>
              )}
              <Link to={`/ledger-reports?account=${detail.JournalLines?.[0]?.account_id || ''}`}>Open in Detail Ledger</Link>
            </Space>
          </div>
        )}
      </Drawer>
    </div>
  );
}
